import React, { createContext, useContext, useState, useEffect } from 'react';
import { getStudents } from '../utils/studentLoader';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [students, setStudents] = useState([]);
    const [progress, setProgress] = useState({});

    // Initial Data Load
    useEffect(() => {
        const loadData = async () => {
            try {
                // 1. Load Local Avatars (Source of Truth for existence for now)
                const localStudents = getStudents();

                // 2. Sync with Supabase (Ensure they exist in DB)
                const { data: dbStudents, error: fetchError } = await supabase.from('students').select('*');

                if (fetchError) {
                    console.error('Error fetching students:', fetchError);
                    // Fallback to local if DB fails
                    setStudents(localStudents);
                } else {
                    // Simple Sync: If DB is empty or missing students, insert them
                    const newStudents = [];
                    const updates = [];

                    for (const localS of localStudents) {
                        const existing = dbStudents?.find(dbS => dbS.name === localS.name);
                        if (!existing) {
                            newStudents.push({ name: localS.name, avatar: localS.avatar });
                        } else if (existing.avatar !== localS.avatar) {
                            updates.push({ ...existing, avatar: localS.avatar });
                        }
                    }

                    if (newStudents.length > 0) {
                        await supabase.from('students').insert(newStudents);
                    }

                    if (updates.length > 0) {
                        for (const update of updates) {
                            await supabase.from('students').update({ avatar: update.avatar }).eq('id', update.id);
                        }
                    }

                    // reload full list
                    const { data: finalStudents } = await supabase.from('students').select('*');
                    setStudents(finalStudents || localStudents);
                }

                // 3. Load All Progress
                const { data: progressData } = await supabase.from('progress').select('*, students(name)');

                const progressMap = {};
                if (progressData) {
                    progressData.forEach(item => {
                        const studentName = item.students?.name;
                        if (studentName) {
                            if (!progressMap[studentName]) progressMap[studentName] = {};
                            progressMap[studentName][item.unit_id] = {
                                score: item.score,
                                unlocked: item.unlocked,
                                date: item.updated_at
                            };
                        }
                    });
                }
                setProgress(progressMap);
            } catch (err) {
                console.error("Critical error in loadData:", err);
                const localStudents = getStudents();
                setStudents(localStudents);
            }
        };

        loadData();
    }, []);

    const login = (student) => {
        setUser({ ...student, role: 'student' });
    };

    const loginAsTeacher = () => {
        setUser({ name: 'Profesor', role: 'teacher' });
    };

    const logout = () => {
        setUser(null);
    };

    const saveProgress = async (unitId, score) => {
        if (!user || user.role === 'teacher') return;

        // Optimistic UI Update first
        const newProgress = { ...progress };
        if (!newProgress[user.name]) newProgress[user.name] = {};

        const currentScore = newProgress[user.name][unitId]?.score || 0;

        if (score > currentScore) {
            newProgress[user.name][unitId] = {
                score,
                date: new Date().toISOString(),
                unlocked: true
            };
            setProgress(newProgress);

            // Persist to Supabase
            // We need the student UUID, assuming 'user' object has 'id' from the DB select
            if (user.id) {
                const { error } = await supabase.from('progress').upsert({
                    student_id: user.id,
                    unit_id: unitId,
                    score: score,
                    unlocked: true,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'student_id, unit_id' });

                if (error) console.error("Error saving progress:", error);
            }
        }
    };

    const refreshProgress = async () => {
        try {
            if (!supabase) return;
            const { data: progressData, error } = await supabase.from('progress').select('*, students(name)');
            if (error) {
                console.error("Error refreshing progress:", error);
                return;
            }
            const progressMap = {};
            if (progressData) {
                progressData.forEach(item => {
                    const studentName = item.students?.name;
                    if (studentName) {
                        if (!progressMap[studentName]) progressMap[studentName] = {};
                        progressMap[studentName][item.unit_id] = {
                            score: item.score,
                            unlocked: item.unlocked,
                            date: item.updated_at
                        };
                    }
                });
            }
            setProgress(progressMap);
        } catch (err) {
            console.error("Error in refreshProgress:", err);
        }
    };

    const updateTeacherPin = (newPin) => {
        if (!newPin || newPin.length !== 4) return false;
        localStorage.setItem('geoceuta_teacher_pin', newPin);
        return true;
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            students, 
            login, 
            loginAsTeacher, 
            logout, 
            progress, 
            saveProgress, 
            refreshProgress, 
            updateTeacherPin 
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
